import {
  ApiChartTemplate,
  ApiChartType,
  ApiDimension,
  ApiYAxisChartType,
  ApiMetric,
} from "@incendium/api";
import { Settings } from "@mui/icons-material";
import {
  Button,
  Grid,
  Portal,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import Loading from "Components/Loading/Loading";
import { AnalyticsSpacing, PageSubAction } from "consts";
import AnalyticsCard from "features/analytics/components/AnalyticsCard";
import { FilterAlt } from "@mui/icons-material";
import {
  DimensionExplorerDrawer,
  table5Rows,
  useSelectedAttributes,
} from "features/analytics";
import { useCallback, useState } from "react";
import { useMount } from "react-use";
import { useDimensionName } from "features/analytics/hooks/useDimensionName";
import { StyledDivider } from "Components/UI/StyledDivider";
import useAppendSearchState from "Hooks/useAppendSearchState";
import AnalyticsCardGroup from "./AnalyticsCardGroup";

function DimensionExplorer() {
  const [selectedDimension, setSelectedDimension] =
    useAppendSearchState<ApiDimension>("d", ApiDimension.DIMENSION_CHANNEL);
  const { selectedAttributes, setSelectedAttributes } = useSelectedAttributes();
  const [open, setOpen] = useState(false);
  const [searchParamsLoaded, setSearchParamsLoaded] = useState(false);
  const dimensionToName = useDimensionName();

  useMount(() => {
    const interval = setInterval(() => {
      const params = new URLSearchParams(window.location.search);
      if (params.has("at") && params.has("d")) {
        setSearchParamsLoaded(true);
        clearInterval(interval);
      }
    }, 100);

    return () => {
      clearInterval(interval);
    };
  });

  const onClose = useCallback(() => {
    setOpen(false);
  }, []);

  if (!searchParamsLoaded) {
    return <Loading fullHeight />;
  }

  return (
    <>
      <Portal container={() => document.getElementById(PageSubAction)}>
        <Stack
          direction="row"
          spacing={2}
          sx={{ width: "100%", marginX: 0, minWidth: 550 }}
          justifyContent={"flex-end"}
          alignItems={"center"}
        >
          <Tooltip
            title={
              <Stack spacing={1}>
                <Typography color="inherit" variant="subtitle2">
                  {dimensionToName(selectedDimension as ApiDimension)}
                </Typography>
                {selectedAttributes.length > 0 && (
                  <>
                    <StyledDivider />
                    <Typography color="inherit" variant="subtitle2">
                      Filters
                    </Typography>
                    <Stack>
                      {selectedAttributes.map((attribute) => (
                        <Typography
                          key={attribute.key! + attribute.value!}
                          color="inherit"
                          variant="body2"
                        >
                          {dimensionToName(attribute.key as ApiDimension)} -{" "}
                          {attribute.value}
                        </Typography>
                      ))}
                    </Stack>
                  </>
                )}
              </Stack>
            }
            arrow
            placement="left"
          >
            <FilterAlt color="primary" />
          </Tooltip>

          <Button
            size="small"
            startIcon={<Settings />}
            onClick={() => setOpen(true)}
          >
            Configure
          </Button>
        </Stack>
      </Portal>

      {selectedDimension ? (
        <Stack spacing={2}>
          {/* Traffic & Acquisition Section */}
          <AnalyticsCardGroup
            localStorageKey="dimension-explorer-traffic-acquisition"
            title="Traffic & Acquisition"
            renderBody={() => (
              <Grid container spacing={AnalyticsSpacing} mb={4}>
                {/* Row 1: Dimension by revenue trended (70%) and by week stacked (30%) */}
                <Grid item xs={8} sx={{ minHeight: 400 }}>
                  <AnalyticsCard
                    chart={{
                      name: "Revenue by Dimension Over Time",
                      dimension: [
                        ApiDimension.DIMENSION_SESSION_START_BY_DAY,
                        selectedDimension,
                      ],
                      template: ApiChartTemplate.TREND,
                      type: ApiChartType.GRAPH,
                      attributes: selectedAttributes || [],
                      comparison: true,
                      limit: 5,
                      yAxisKeys: [
                        {
                          key: "l",
                          fields: [ApiMetric.METRIC_REVENUE],
                          chart: {
                            [ApiMetric.METRIC_REVENUE]: ApiYAxisChartType.AREA,
                          },
                        },
                      ],
                      displayOptions: {
                        noAttributionToggle: true,
                        disabledDimensionClick: true,
                        noFilters: true,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <AnalyticsCard
                    chart={{
                      name: "Revenue by Week",
                      dimension: [
                        ApiDimension.DIMENSION_SESSION_START_BY_WEEK,
                        selectedDimension,
                      ],
                      template: ApiChartTemplate.TREND,
                      type: ApiChartType.GRAPH,
                      comparison: true,
                      limit: 5,
                      attributes: selectedAttributes || [],
                      yAxisKeys: [
                        {
                          key: "l",
                          fields: [ApiMetric.METRIC_REVENUE],
                          stackId: "l",
                          chart: {
                            [ApiMetric.METRIC_REVENUE]: ApiYAxisChartType.BAR,
                          },
                        },
                      ],
                      displayOptions: {
                        noAttributionToggle: true,
                        disabledDimensionClick: true,
                        noFilters: true,
                      },
                    }}
                  />
                </Grid>

                {/* Row 2: Sessions by week stacked bar (30%) and Sessions by day line (70%) */}
                <Grid item xs={4} sx={{ minHeight: 400 }}>
                  <AnalyticsCard
                    chart={{
                      name: "Sessions by Week",
                      dimension: [
                        ApiDimension.DIMENSION_SESSION_START_BY_WEEK,
                        selectedDimension,
                      ],
                      template: ApiChartTemplate.TREND,
                      type: ApiChartType.GRAPH,
                      attributes: selectedAttributes || [],
                      comparison: true,
                      limit: 5,
                      yAxisKeys: [
                        {
                          key: "l",
                          fields: [ApiMetric.METRIC_SESSIONS_COUNT],
                          stackId: "l",
                          chart: {
                            [ApiMetric.METRIC_SESSIONS_COUNT]:
                              ApiYAxisChartType.BAR,
                          },
                        },
                      ],
                      displayOptions: {
                        noAttributionToggle: true,
                        disabledDimensionClick: true,
                        noFilters: true,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={8} sx={{ minHeight: 400 }}>
                  <AnalyticsCard
                    chart={{
                      name: "Sessions by Day",
                      dimension: [
                        ApiDimension.DIMENSION_SESSION_START_BY_DAY,
                        selectedDimension,
                      ],
                      template: ApiChartTemplate.TREND,
                      type: ApiChartType.GRAPH,
                      attributes: selectedAttributes || [],
                      comparison: true,
                      limit: 5,
                      yAxisKeys: [
                        {
                          key: "l",
                          fields: [ApiMetric.METRIC_SESSIONS_COUNT],
                          chart: {
                            [ApiMetric.METRIC_SESSIONS_COUNT]:
                              ApiYAxisChartType.LINE,
                          },
                        },
                      ],
                      displayOptions: {
                        noAttributionToggle: true,
                        disabledDimensionClick: true,
                        noFilters: true,
                      },
                    }}
                  />
                </Grid>

                {/* Row 3: New customers by day line (70%) and CAC bar chart (30%) */}
                <Grid item xs={8} sx={{ minHeight: 400 }}>
                  <AnalyticsCard
                    chart={{
                      name: "New Customers by Day",
                      dimension: [
                        ApiDimension.DIMENSION_SESSION_START_BY_DAY,
                        selectedDimension,
                      ],
                      template: ApiChartTemplate.TREND,
                      type: ApiChartType.GRAPH,
                      attributes: selectedAttributes || [],
                      comparison: true,
                      limit: 5,
                      includePublisherReadyOnly: true,
                      yAxisKeys: [
                        {
                          key: "l",
                          fields: [ApiMetric.METRIC_NEW_CUSTOMERS_COUNT],
                          chart: {
                            [ApiMetric.METRIC_NEW_CUSTOMERS_COUNT]:
                              ApiYAxisChartType.LINE,
                          },
                        },
                      ],
                      displayOptions: {
                        noAttributionToggle: true,
                        disabledDimensionClick: true,
                        noFilters: true,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={4} sx={{ minHeight: 400 }}>
                  <AnalyticsCard
                    chart={{
                      name: "CAC Comparison",
                      dimension: [selectedDimension],
                      template: ApiChartTemplate.SNAPSHOT,
                      type: ApiChartType.GRAPH,
                      attributes: selectedAttributes || [],
                      comparison: true,
                      yAxisKeys: [
                        {
                          key: "l",
                          fields: [ApiMetric.METRIC_CAC],
                          chart: {
                            [ApiMetric.METRIC_CAC]: ApiYAxisChartType.BAR,
                          },
                        },
                      ],
                      limit: 15,
                      displayOptions: {
                        noAttributionToggle: true,
                        disabledDimensionClick: true,
                        noFilters: true,
                      },
                    }}
                  />
                </Grid>
              </Grid>
            )}
          />
          {/* Funnel Performance Section */}
          <AnalyticsCardGroup
            localStorageKey="dimension-explorer-funnel-performance"
            title="Funnel Performance"
            renderBody={() => (
              <Grid
                container
                spacing={AnalyticsSpacing}
                mb={4}
                sx={{ minHeight: table5Rows }}
              >
                <Grid item xs={12}>
                  <AnalyticsCard
                    chart={{
                      name: "Funnel Metrics",
                      dimension: [selectedDimension],
                      template: ApiChartTemplate.TABLE,
                      type: ApiChartType.TABLE,
                      attributes: selectedAttributes || [],
                      comparison: true,
                      yAxisKeys: [
                        {
                          key: "l",
                          fields: [
                            ApiMetric.METRIC_SESSIONS_COUNT,
                            ApiMetric.METRIC_PRODUCT_PAGEVIEWS_COUNT,
                            ApiMetric.METRIC_SESSION_ADD_TO_CART_CLICK_RATE,
                            ApiMetric.METRIC_CART_ABANDONMENT_RATE,
                            ApiMetric.METRIC_VISIT_TO_PURCHASE_RATE,
                            ApiMetric.METRIC_REVENUE,
                          ],
                        },
                      ],
                      displayOptions: {
                        noAttributionToggle: true,
                        disabledDimensionClick: true,
                        noFilters: true,
                        showTotals: true,
                        rowsPerPage: 0,
                      },
                    }}
                  />
                </Grid>
              </Grid>
            )}
          />

          {/* Engagement Section */}
          <AnalyticsCardGroup
            localStorageKey="dimension-explorer-engagement"
            title="Engagement"
            renderBody={() => (
              <Grid
                container
                spacing={AnalyticsSpacing}
                mb={4}
                sx={{ minHeight: table5Rows }}
              >
                <Grid item xs={12}>
                  <AnalyticsCard
                    chart={{
                      name: "Engagement Metrics",
                      dimension: [selectedDimension],
                      template: ApiChartTemplate.TABLE,
                      type: ApiChartType.TABLE,
                      attributes: selectedAttributes || [],
                      comparison: true,
                      yAxisKeys: [
                        {
                          key: "l",
                          fields: [
                            ApiMetric.METRIC_SESSIONS_COUNT,
                            ApiMetric.METRIC_EFFECTIVE_SESSIONS_COUNT,
                            ApiMetric.METRIC_MACRO_CONVERSION_RATE,
                            ApiMetric.METRIC_PAGE_MICRO_CONVERSION_RATE,
                            ApiMetric.METRIC_AVERAGE_SCROLL_PERCENTAGE,
                            ApiMetric.METRIC_AVERAGE_SESSION_DURATION,
                            ApiMetric.METRIC_AVERAGE_PAGEVIEWS_PER_SESSION,
                            ApiMetric.METRIC_BOUNCE_RATE,
                          ],
                        },
                      ],
                      displayOptions: {
                        noAttributionToggle: true,
                        disabledDimensionClick: true,
                        noFilters: true,
                        showTotals: true,
                        rowsPerPage: 0,
                      },
                    }}
                  />
                </Grid>
              </Grid>
            )}
          />

          {/* Retention Section */}
          <AnalyticsCardGroup
            localStorageKey="dimension-explorer-retention"
            title="Retention"
            renderBody={() => (
              <Grid
                container
                spacing={AnalyticsSpacing}
                mb={4}
                sx={{ minHeight: table5Rows }}
              >
                <Grid item xs={12}>
                  <AnalyticsCard
                    chart={{
                      name: "Retention Metrics",
                      dimension: [selectedDimension],
                      template: ApiChartTemplate.TABLE,
                      type: ApiChartType.TABLE,
                      attributes: selectedAttributes || [],
                      comparison: true,
                      yAxisKeys: [
                        {
                          key: "l",
                          fields: [
                            ApiMetric.METRIC_RETURNING_CUSTOMER_SESSION_COUNT,
                            ApiMetric.METRIC_REPEAT_CUSTOMER_RATE,
                            ApiMetric.METRIC_RETURNING_CUSTOMER_RATE,
                            ApiMetric.METRIC_LTV,
                          ],
                        },
                      ],
                      displayOptions: {
                        noAttributionToggle: true,
                        disabledDimensionClick: true,
                        noFilters: true,
                        showTotals: true,
                        rowsPerPage: 0,
                      },
                    }}
                  />
                </Grid>
              </Grid>
            )}
          />
        </Stack>
      ) : (
        <Loading />
      )}
      <DimensionExplorerDrawer
        open={open}
        onClose={onClose}
        dimension={selectedDimension}
        setDimension={setSelectedDimension}
        attributes={selectedAttributes}
        setAttributes={setSelectedAttributes}
      />
    </>
  );
}

export default DimensionExplorer;
